"use client";
import { KeyboardEvent, useEffect, useRef, useState, useId } from "react";
import { cn } from "@/lib/utils";
import type { TooltipProps } from "../types";

export function ClickableTooltip({
  content,
  children,
  className = "",
  contentClassName = "",
  mobileAlignment = "center",
  ariaLabel = "More information",
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipId = useId();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        event.target instanceof Element &&
        !tooltipRef.current.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    if (isVisible && (event.key === "Escape" || event.key === "Esc")) {
      setIsVisible(false);
      buttonRef?.current?.focus();
    }
  };

  const tooltipAlignment = {
    right: {
      content: "-translate-x-[80%]",
      arrow: "left-[80%]",
    },
    center: {
      content: "-translate-x-1/2",
      arrow: "left-1/2",
    },
    left: {
      content: "-translate-x-[20%]",
      arrow: "left-[20%]",
    },
  };

  return (
    <div ref={tooltipRef} className="relative inline-block">
      <button
        ref={buttonRef}
        className={`no-underline text-inherit hover:text-inherit ${className}`}
        onClick={() => setIsVisible(!isVisible)}
        onKeyDown={handleKeyDown}
        aria-expanded={isVisible}
        aria-controls={tooltipId}
        aria-label={ariaLabel}
      >
        {/* trigger */}
        {children}
      </button>

      {/* tooltip content */}
      <div
        id={tooltipId}
        tabIndex={isVisible ? 0 : undefined}
        role="tooltip"
        className={cn(
          "max-w-[300px] z-50",
          "rounded bg-white px-1 py-1 shadow-lg",
          "text-sm text-gray-800 whitespace-normal break-words",
          "absolute left-1/2 -top-2 md:-translate-x-1/2 -translate-y-full z-30",
          isVisible ? "" : "h-0 p-0 overflow-hidden",
          tooltipAlignment[mobileAlignment].content,
          contentClassName,
        )}
        aria-hidden={!isVisible}
        {...(!isVisible ? { inert: true } : {})}
        onKeyDown={handleKeyDown}
      >
        <div className="whitespace-normal break-words">{content}</div>
        {/* arrow */}
        <div
          className={cn(
            "h-2 w-2",
            "rotate-45 bg-white shadow-lg",
            "absolute md:left-1/2 top-full -translate-x-1/2 -translate-y-1/2",
            tooltipAlignment[mobileAlignment].arrow,
          )}
        />
      </div>
    </div>
  );
}
